import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useProfile } from '@/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';

const RECOMMENDED_COURSE_OPTIONS = ["Private Class", "Premiere Class", "Premiere Class Inter"];
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, Save } from 'lucide-react';

function RatingField({ label, value, onChange }: { label: string; value: number | null; onChange: (v: number) => void }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm">{label}</Label>
      <RadioGroup
        value={value?.toString() || ''}
        onValueChange={(v) => onChange(parseInt(v))}
        className="flex gap-3"
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <div key={n} className="flex items-center space-x-1">
            <RadioGroupItem value={n.toString()} id={`rating-${label}-${n}`} />
            <Label htmlFor={`rating-${label}-${n}`} className="text-sm cursor-pointer">{n}</Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}

export default function TeacherTrialEvalPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { toast } = useToast();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Read-only info
  const [studentNickname, setStudentNickname] = useState('');
  const [studentName, setStudentName] = useState('');
  const [grade, setGrade] = useState('');
  const [subject, setSubject] = useState('');
  const [classDate, setClassDate] = useState('');

  // Editable Section 5
  const [preScoreReason, setPreScoreReason] = useState('');
  const [postScoreReason, setPostScoreReason] = useState('');
  const [trialOverview, setTrialOverview] = useState('');
  const [studentStrengths, setStudentStrengths] = useState('');
  const [studentImprovements, setStudentImprovements] = useState('');
  const [teachingApproach, setTeachingApproach] = useState('');
  const [recommendedDuration, setRecommendedDuration] = useState('');
  const [recommendedCourse, setRecommendedCourse] = useState('');
  const [trialTopic, setTrialTopic] = useState('');

  // Acknowledgement
  const [teacherAcknowledged, setTeacherAcknowledged] = useState(false);

  // Section 6 ratings
  const [rating11, setRating11] = useState<number | null>(null);
  const [rating12, setRating12] = useState<number | null>(null);
  const [rating13, setRating13] = useState<number | null>(null);
  const [rating21, setRating21] = useState<number | null>(null);
  const [rating22, setRating22] = useState<number | null>(null);
  const [rating23, setRating23] = useState<number | null>(null);
  const [rating31, setRating31] = useState<number | null>(null);
  const [rating32, setRating32] = useState<number | null>(null);
  const [rating33, setRating33] = useState<number | null>(null);
  const [rating41, setRating41] = useState<number | null>(null);
  const [rating42, setRating42] = useState<number | null>(null);
  const [rating43, setRating43] = useState<number | null>(null);

  const totalScore = [rating11, rating12, rating13, rating21, rating22, rating23, rating31, rating32, rating33, rating41, rating42, rating43]
    .filter(v => v !== null)
    .reduce((sum, v) => sum + (v || 0), 0);

  useEffect(() => {
    const fetchTrial = async () => {
      if (!id) return;
      const { data, error } = await supabase
        .from('class_trials')
        .select('*')
        .eq('id', id)
        .single();

      if (error || !data) {
        toast({ title: t('trialEval.notFound'), variant: 'destructive' });
        navigate('/teacher-trials');
        return;
      }

      // Populate read-only
      setStudentNickname(data.student_nickname || '');
      setStudentName(data.student_name || '');
      setGrade(data.grade || '');
      setSubject(data.subject || '');
      setClassDate(data.class_date || '');

      // Populate editable
      setPreScoreReason(data.pre_score_reason || '');
      setPostScoreReason(data.post_score_reason || '');
      setTrialOverview(data.trial_overview || '');
      setStudentStrengths(data.student_strengths || '');
      setStudentImprovements(data.student_improvements || '');
      setTeachingApproach(data.teaching_approach || '');
      setRecommendedDuration(data.recommended_duration || '');
      setRecommendedCourse(data.recommended_course || '');
      setTrialTopic(data.trial_topic || '');
      setTeacherAcknowledged(data.teacher_acknowledged || false);

      setRating11(data.rating_1_1);
      setRating12(data.rating_1_2);
      setRating13(data.rating_1_3);
      setRating21(data.rating_2_1);
      setRating22(data.rating_2_2);
      setRating23(data.rating_2_3);
      setRating31(data.rating_3_1);
      setRating32(data.rating_3_2);
      setRating33(data.rating_3_3);
      setRating41(data.rating_4_1);
      setRating42(data.rating_4_2);
      setRating43(data.rating_4_3);

      setIsLoading(false);
    };
    fetchTrial();
  }, [id, navigate, toast]);

  const handleSave = async () => {
    if (!id) return;
    setIsSaving(true);

    const { error } = await supabase
      .from('class_trials')
      .update({
        pre_score_reason: preScoreReason || null,
        post_score_reason: postScoreReason || null,
        trial_overview: trialOverview || null,
        student_strengths: studentStrengths || null,
        student_improvements: studentImprovements || null,
        teaching_approach: teachingApproach || null,
        recommended_duration: recommendedDuration || null,
        recommended_course: recommendedCourse || null,
        trial_topic: trialTopic || null,
        teacher_acknowledged: teacherAcknowledged,
        rating_1_1: rating11,
        rating_1_2: rating12,
        rating_1_3: rating13,
        rating_2_1: rating21,
        rating_2_2: rating22,
        rating_2_3: rating23,
        rating_3_1: rating31,
        rating_3_2: rating32,
        rating_3_3: rating33,
        rating_4_1: rating41,
        rating_4_2: rating42,
        rating_4_3: rating43,
      })
      .eq('id', id);

    setIsSaving(false);

    if (error) {
      toast({ title: t('trialEval.saveError'), description: error.message, variant: 'destructive' });
    } else {
      toast({ title: t('trialEval.saveSuccess') });
      navigate('/teacher-trials');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-3xl space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/teacher-trials')}>
        <ArrowLeft className="h-4 w-4 mr-1" /> {t('trialEval.back')}
      </Button>

      {/* Student Info (read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('trialEval.studentInfo')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div><span className="text-muted-foreground">{t('trialEval.nickname')}:</span> {studentNickname || '-'}</div>
          <div><span className="text-muted-foreground">{t('trialEval.fullName')}:</span> {studentName || '-'}</div>
          <div><span className="text-muted-foreground">{t('trialEval.grade')}:</span> {grade || '-'}</div>
          <div><span className="text-muted-foreground">{t('trialEval.subject')}:</span> {subject || '-'}</div>
          <div><span className="text-muted-foreground">{t('trialEval.classDate')}:</span> {classDate || '-'}</div>
        </CardContent>
      </Card>

      {/* Teacher Acknowledgement */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('trialEval.ackTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="space-y-3 leading-relaxed">
            <p>{t('trialEval.ack.intro1')}</p>
            <p>{t('trialEval.ack.intro2')}</p>
            <p>{t('trialEval.ack.intro3')}</p>
          </div>

          <div className="space-y-3 leading-relaxed border-t pt-4">
            <h3 className="font-semibold">{t('trialEval.ack.prepTitle')}</h3>
            <p><strong>{t('trialEval.ack.prep1Title')}</strong><br />{t('trialEval.ack.prep1')}</p>
            <p><strong>{t('trialEval.ack.prep2Title')}</strong><br />{t('trialEval.ack.prep2')}</p>
            <p><strong>{t('trialEval.ack.prep3Title')}</strong><br />{t('trialEval.ack.prep3')}</p>
          </div>

          <div className="space-y-3 leading-relaxed border-t pt-4">
            <h3 className="font-semibold">{t('trialEval.ack.duringTitle')}</h3>
            <p><strong>{t('trialEval.ack.during1Title')}</strong><br />{t('trialEval.ack.during1')}</p>
            <p><strong>{t('trialEval.ack.during2Title')}</strong><br />{t('trialEval.ack.during2')}</p>
            <p><strong>{t('trialEval.ack.during3Title')}</strong><br />{t('trialEval.ack.during3')}</p>
            <p><strong>{t('trialEval.ack.during4Title')}</strong><br />{t('trialEval.ack.during4')}</p>
            <p><strong>{t('trialEval.ack.during5Title')}</strong><br />{t('trialEval.ack.during5')}</p>
            <p><strong>{t('trialEval.ack.during6Title')}</strong><br />{t('trialEval.ack.during6')}</p>
          </div>

          <div className="flex items-start gap-2 border-t pt-4">
            <Checkbox
              id="teacher-acknowledged"
              checked={teacherAcknowledged}
              onCheckedChange={(v) => setTeacherAcknowledged(v === true)}
            />
            <Label htmlFor="teacher-acknowledged" className="cursor-pointer leading-relaxed">
              {t('trialEval.ackCheckbox')}
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Section 5: Teacher Evaluation */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('trialEval.section5Title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('trialEval.trialTopic')}</Label>
            <Input value={trialTopic} onChange={(e) => setTrialTopic(e.target.value)} placeholder={t('trialEval.trialTopicPlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.preScore')}</Label>
            <Textarea value={preScoreReason} onChange={(e) => setPreScoreReason(e.target.value)} rows={2} placeholder={t('trialEval.preScorePlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.postScore')}</Label>
            <p className="text-xs text-muted-foreground">{t('trialEval.postScoreHint')}</p>
            <Textarea value={postScoreReason} onChange={(e) => setPostScoreReason(e.target.value)} rows={2} placeholder={t('trialEval.postScorePlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.overview')}</Label>
            <Textarea value={trialOverview} onChange={(e) => setTrialOverview(e.target.value)} rows={3} placeholder={t('trialEval.overviewPlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.strengths')}</Label>
            <Textarea value={studentStrengths} onChange={(e) => setStudentStrengths(e.target.value)} rows={2} placeholder={t('trialEval.strengthsPlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.improvements')}</Label>
            <Textarea value={studentImprovements} onChange={(e) => setStudentImprovements(e.target.value)} rows={2} placeholder={t('trialEval.improvementsPlaceholder')} />
          </div>
          <div className="space-y-2">
            <Label>{t('trialEval.approach')}</Label>
            <Textarea value={teachingApproach} onChange={(e) => setTeachingApproach(e.target.value)} rows={2} placeholder={t('trialEval.approachPlaceholder')} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('trialEval.duration')}</Label>
              <Input value={recommendedDuration} onChange={(e) => setRecommendedDuration(e.target.value)} placeholder={t('trialEval.durationPlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('trialEval.course')}</Label>
              <p className="text-xs text-muted-foreground">{t('trialEval.courseHint')}</p>
              <Select value={recommendedCourse} onValueChange={setRecommendedCourse}>
                <SelectTrigger><SelectValue placeholder={t('trialEval.selectCourse')} /></SelectTrigger>
                <SelectContent>
                  {RECOMMENDED_COURSE_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 6: Behavior Audit Ratings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('trialEval.section6Title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-1">{t('trialEval.part1')}</h3>
            <RatingField label={t('trialEval.q11')} value={rating11} onChange={setRating11} />
            <RatingField label={t('trialEval.q12')} value={rating12} onChange={setRating12} />
            <RatingField label={t('trialEval.q13')} value={rating13} onChange={setRating13} />
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-1">{t('trialEval.part2')}</h3>
            <RatingField label={t('trialEval.q21')} value={rating21} onChange={setRating21} />
            <RatingField label={t('trialEval.q22')} value={rating22} onChange={setRating22} />
            <RatingField label={t('trialEval.q23')} value={rating23} onChange={setRating23} />
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-1">{t('trialEval.part3')}</h3>
            <RatingField label={t('trialEval.q31')} value={rating31} onChange={setRating31} />
            <RatingField label={t('trialEval.q32')} value={rating32} onChange={setRating32} />
            <RatingField label={t('trialEval.q33')} value={rating33} onChange={setRating33} />
          </div>
          <div className="space-y-3">
            <h3 className="font-semibold text-sm border-b pb-1">{t('trialEval.part4')}</h3>
            <RatingField label={t('trialEval.q41')} value={rating41} onChange={setRating41} />
            <RatingField label={t('trialEval.q42')} value={rating42} onChange={setRating42} />
            <RatingField label={t('trialEval.q43')} value={rating43} onChange={setRating43} />
          </div>

          <div className="bg-muted rounded-lg p-4 text-center">
            <p className="text-sm text-muted-foreground">{t('trialEval.totalScore')}</p>
            <p className="text-2xl font-bold">{totalScore} / 60</p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3 pb-8">
        <Button variant="outline" onClick={() => navigate('/teacher-trials')}>{t('trialEval.cancel')}</Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          {t('trialEval.save')}
        </Button>
      </div>
    </div>
  );
}
